import { sortByName, sortAsc } from '../utils/sortOptions';
import { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { MagnifyingGlassIcon, ArrowPathIcon, ChevronDownIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

const TEACHER_MODULES = [
  { key:'dashboard',        label:'Dashboard',         icon:'🏠', desc:'Teacher home with stats and charts' },
  { key:'classes',          label:'My Classes',        icon:'👥', desc:'View assigned classes and students' },
  { key:'attendance',       label:'My Attendance',     icon:'📅', desc:'Check-in/out and own attendance records' },
  { key:'assessments',      label:'Assessments',       icon:'📝', desc:'Create and manage student assessments' },
  { key:'gradeReports',     label:'Grade Reports',     icon:'📊', desc:'View student grade reports' },
  { key:'studentAttendance',label:'Student Attendance',icon:'✅', desc:'Mark and view student attendance' },
  { key:'datesheets',       label:'Datesheets',        icon:'🗓️', desc:'View exam datesheets' },
  { key:'materials',        label:'Materials',         icon:'📚', desc:'Upload and manage teaching materials' },
  { key:'quizzes',          label:'AI Quizzes',        icon:'🤖', desc:'Create AI and manual quizzes' },
  { key:'notices',          label:'Notices',           icon:'📢', desc:'View and post notices' },
  { key:'salary',           label:'Salary',            icon:'💰', desc:'View own salary and history' },
  { key:'leaves',           label:'Leaves',            icon:'🏖️', desc:'Apply and manage leaves' },
  { key:'profile',          label:'Profile',           icon:'👤', desc:'View and update profile' },
];

const STUDENT_MODULES = [
  { key:'dashboard',   label:'Dashboard',         icon:'🏠', desc:'Student home with quick stats' },
  { key:'attendance',  label:'Attendance',        icon:'📅', desc:'View attendance records and apply leave' },
  { key:'results',     label:'Results',           icon:'🎯', desc:'View marks and grade reports' },
  { key:'fees',        label:'Fees',              icon:'💵', desc:'View fee bills and submit payment proof' },
  { key:'materials',   label:'Materials',         icon:'📚', desc:'Access study materials by subject' },
  { key:'quizzes',     label:'Online Assessments',icon:'📋', desc:'Attempt assigned quizzes' },
  { key:'notices',     label:'Notices',           icon:'📢', desc:'View school and teacher notices' },
  { key:'timetable',   label:'Timetable',         icon:'🕐', desc:'View class timetable with times' },
  { key:'datesheets',  label:'Datesheets',        icon:'🗓️', desc:'View exam schedules' },
  { key:'profile',     label:'Profile',           icon:'👤', desc:'View and update profile' },
];

const FINANCE_MODULES = [
  { key:'dashboard',       label:'Dashboard',        icon:'🏠', desc:'Finance home with stats and charts' },
  { key:'attendance',      label:'Attendance',       icon:'📅', desc:'Own check-in/out and attendance records' },
  { key:'leaves',          label:'Leaves',           icon:'🏖️', desc:'Apply and manage own leaves' },
  { key:'staffAttendance', label:'Staff Attendance', icon:'✅', desc:'View staff attendance records' },
  { key:'staffLeaves',     label:'Staff Leaves',     icon:'📋', desc:'Review staff leave requests' },
  { key:'staffManagement', label:'Staff Management', icon:'🏢', desc:'Manage staff accounts' },
  { key:'fees',            label:'Fees',             icon:'💵', desc:'Manage student fee bills and payments' },
  { key:'staffSalary',     label:'Staff Salary',     icon:'📊', desc:'View and manage staff salary records' },
  { key:'mySalary',        label:'My Salary',        icon:'💰', desc:'View own salary and history' },
  { key:'expenses',        label:'Expenses',         icon:'💲', desc:'Track school expenses' },
  { key:'assets',          label:'Assets',           icon:'📦', desc:'Track school assets' },
  { key:'biometric',       label:'Biometric & RFID', icon:'🔒', desc:'Manage biometric and RFID devices' },
  { key:'studentGateLog',  label:'Student Gate Log', icon:'🪪', desc:'View student RFID gate logs' },
  { key:'letters',         label:'Letters',          icon:'📄', desc:'Generate and manage letters' },
  { key:'notices',         label:'Notices',          icon:'📢', desc:'View and post notices' },
  { key:'requests',        label:'My Requests',      icon:'📥', desc:'Track submitted requests' },
  { key:'profile',         label:'Profile',          icon:'👤', desc:'View and update profile' },
];

const LIBRARIAN_MODULES = [
  { key:'dashboard',    label:'Dashboard',    icon:'🏠', desc:'Librarian home with stats and charts' },
  { key:'catalog',      label:'Book Catalog', icon:'📚', desc:'Manage the book catalog' },
  { key:'issueReturn',  label:'Issue / Return', icon:'🔄', desc:'Issue and return books' },
  { key:'fines',        label:'Fines',        icon:'💵', desc:'Manage library fines' },
  { key:'myAttendance', label:'My Attendance',icon:'📅', desc:'Own check-in/out and attendance records' },
  { key:'mySalary',     label:'My Salary',    icon:'💰', desc:'View own salary and history' },
  { key:'myLeaves',     label:'My Leaves',    icon:'🏖️', desc:'Apply and manage own leaves' },
  { key:'profile',      label:'Profile',      icon:'👤', desc:'View and update profile' },
];

const HOSTEL_MODULES = [
  { key:'dashboard',    label:'Dashboard',    icon:'🏠', desc:'Hostel home with stats and charts' },
  { key:'rooms',        label:'Rooms',        icon:'🏨', desc:'Manage hostel rooms' },
  { key:'allocations',  label:'Allocation',   icon:'🛏️', desc:'Allocate and vacate students' },
  { key:'fines',        label:'Fines',        icon:'💵', desc:'Manage hostel fines' },
  { key:'myAttendance', label:'My Attendance',icon:'📅', desc:'Own check-in/out and attendance records' },
  { key:'mySalary',     label:'My Salary',    icon:'💰', desc:'View own salary and history' },
  { key:'myLeaves',     label:'My Leaves',    icon:'🏖️', desc:'Apply and manage own leaves' },
  { key:'profile',      label:'Profile',      icon:'👤', desc:'View and update profile' },
];

const MODULE_DEFS = { teacher: TEACHER_MODULES, student: STUDENT_MODULES, finance: FINANCE_MODULES, librarian: LIBRARIAN_MODULES, hostel_member: HOSTEL_MODULES };
const ROLE_FIELD   = { teacher: 'teacherModules', student: 'studentModules', finance: 'financeModules', librarian: 'librarianModules', hostel_member: 'hostelModules' };
const ROLE_LABEL   = { teacher: 'Teachers', student: 'Students', finance: 'Finance', librarian: 'Librarians', hostel_member: 'Hostel Members' };

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
  .cap*{box-sizing:border-box;}
  .cap{font-family:'Sora',sans-serif;color:#1a1d2e;animation:capFade .4s ease both;}
  @keyframes capFade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  .cap-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1.5rem;flex-wrap:wrap;gap:1rem;}
  .cap-title{font-size:1.35rem;font-weight:700;letter-spacing:-.02em;color:#0f1123;}
  .cap-sub{font-size:.76rem;color:#9094a8;margin-top:3px;}
  .req-banner{background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;padding:10px 14px;font-size:.76rem;color:#c2410c;margin-bottom:1.4rem;display:flex;align-items:center;gap:8px;}
  .cap-tabs{display:flex;gap:4px;background:#f0f2f7;border-radius:12px;padding:4px;margin-bottom:1.4rem;width:fit-content;}
  .cap-tab{padding:8px 20px;border-radius:9px;border:none;font-family:'Sora',sans-serif;font-size:.82rem;font-weight:600;cursor:pointer;transition:all .15s;background:transparent;color:#9094a8;white-space:nowrap;}
  .cap-tab.active{background:white;color:#0f1123;box-shadow:0 1px 4px rgba(0,0,0,.12);}
  .sub-tabs{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:1.2rem;}
  .sub-tab{padding:6px 16px;border-radius:99px;border:1.5px solid #e0e4f0;background:white;font-family:'Sora',sans-serif;font-size:.76rem;font-weight:600;cursor:pointer;transition:all .15s;color:#6b7194;}
  .sub-tab:hover{border-color:#f97316;color:#f97316;}
  .sub-tab.active{background:#f97316;color:white;border-color:#f97316;}
  .role-card{background:white;border-radius:16px;border:1px solid #e8ebf4;padding:1.2rem 1.4rem;margin-bottom:1rem;}
  .role-card-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:1rem;flex-wrap:wrap;gap:8px;}
  .role-card-title{font-size:.9rem;font-weight:700;color:#0f1123;}
  .role-card-sub{font-size:.72rem;color:#9094a8;margin-top:2px;}
  .module-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:10px;}
  .module-item{display:flex;align-items:center;justify-content:space-between;padding:10px 14px;border-radius:10px;border:1.5px solid #e8ebf4;background:#fafbff;gap:8px;transition:all .15s;}
  .module-item.enabled{border-color:#10c98e;background:#f0fdf8;}
  .module-item.disabled{border-color:#e8ebf4;background:#fafbff;opacity:.75;}
  .module-item-left{display:flex;align-items:center;gap:10px;min-width:0;}
  .module-icon{font-size:1.1rem;flex-shrink:0;}
  .module-label{font-size:.8rem;font-weight:600;color:#0f1123;}
  .module-desc{font-size:.68rem;color:#9094a8;margin-top:1px;}
  .toggle{position:relative;width:40px;height:22px;flex-shrink:0;}
  .toggle input{opacity:0;width:0;height:0;}
  .toggle-slider{position:absolute;cursor:pointer;inset:0;background:#d1d5db;border-radius:99px;transition:.2s;}
  .toggle-slider:before{content:'';position:absolute;width:16px;height:16px;left:3px;top:3px;background:white;border-radius:50%;transition:.2s;}
  .toggle input:checked+.toggle-slider{background:#10c98e;}
  .toggle input:checked+.toggle-slider:before{transform:translateX(18px);}
  .btn{display:inline-flex;align-items:center;gap:7px;padding:8px 14px;border:none;border-radius:9px;font-family:'Sora',sans-serif;font-size:.78rem;font-weight:600;cursor:pointer;transition:all .18s;}
  .btn svg{width:14px;height:14px;stroke-width:2;}
  .btn-orange{background:#f97316;color:white;} .btn-orange:hover{background:#ea6c0a;}
  .btn-ghost{background:#f0f2f7;color:#6b7194;} .btn-ghost:hover{background:#e4e7f0;}
  .btn-sm{padding:5px 10px;font-size:.73rem;} .btn:disabled{opacity:.5;cursor:not-allowed;}
  .filter-bar{display:flex;gap:10px;flex-wrap:wrap;margin-bottom:1.2rem;align-items:center;}
  .search-wrap{position:relative;flex:1;min-width:200px;}
  .search-wrap svg{position:absolute;left:10px;top:50%;transform:translateY(-50%);width:15px;height:15px;stroke:#9094a8;stroke-width:2;}
  .search-input{width:100%;padding:9px 12px 9px 34px;border:1.5px solid #e0e4f0;border-radius:10px;font-family:'Sora',sans-serif;font-size:.82rem;background:#f8f9fc;color:#1a1d2e;outline:none;}
  .search-input:focus{border-color:#f97316;background:white;}
  .filter-select{padding:9px 12px;border:1.5px solid #e0e4f0;border-radius:10px;font-family:'Sora',sans-serif;font-size:.82rem;background:#f8f9fc;color:#1a1d2e;outline:none;}
  .filter-select:focus{border-color:#f97316;}
  .panel{background:white;border-radius:16px;border:1px solid #e8ebf4;overflow:hidden;margin-bottom:1rem;}
  .panel-head{display:flex;align-items:center;justify-content:space-between;padding:14px 18px;border-bottom:1px solid #f0f2f8;flex-wrap:wrap;gap:8px;}
  .panel-title{font-size:.86rem;font-weight:600;color:#1a1d2e;}
  .person-row{display:flex;align-items:center;justify-content:space-between;padding:12px 18px;border-bottom:1px solid #f5f6fb;gap:10px;flex-wrap:wrap;}
  .person-row:last-child{border-bottom:none;} .person-row:hover{background:#fafbff;}
  .p-avatar{width:32px;height:32px;border-radius:9px;background:#fff7ed;color:#f97316;display:flex;align-items:center;justify-content:center;font-size:.68rem;font-weight:700;flex-shrink:0;}
  .p-name{font-size:.82rem;font-weight:600;color:#0f1123;}
  .p-meta{font-size:.7rem;color:#9094a8;margin-top:1px;}
  .p-badge{display:inline-flex;font-size:.65rem;font-weight:600;padding:2px 8px;border-radius:99px;}
  .p-badge.override{background:#fef3c7;color:#92400e;}
  .p-badge.default{background:#f0f2f7;color:#6b7280;}
  .p-badge.class-ov{background:#ede9fe;color:#7c3aed;}
  .p-badge.section-ov{background:#dbeafe;color:#1d4ed8;}
  .override-drawer{background:#f8f9fc;border-top:1px solid #f0f2f8;padding:14px 18px;}
  .od-title{font-size:.76rem;font-weight:700;color:#1a1d2e;margin-bottom:10px;}
  .od-module-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:8px;margin-bottom:10px;}
  .od-module{display:flex;align-items:center;gap:8px;padding:7px 10px;border-radius:8px;border:1.5px solid #e0e4f0;background:white;font-size:.76rem;font-weight:500;cursor:pointer;transition:all .13s;user-select:none;}
  .od-module.on{border-color:#10c98e;background:#f0fdf8;color:#0f1123;}
  .od-module.off{border-color:#e0e4f0;background:white;color:#9094a8;}
  .od-module .dot{width:10px;height:10px;border-radius:50%;flex-shrink:0;}
  .od-module.on .dot{background:#10c98e;} .od-module.off .dot{background:#d1d5db;}
  .od-actions{display:flex;gap:8px;flex-wrap:wrap;}
  .class-section{background:white;border-radius:14px;border:1px solid #e8ebf4;margin-bottom:10px;overflow:hidden;}
  .class-section-head{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;cursor:pointer;gap:8px;flex-wrap:wrap;}
  .class-section-head:hover{background:#fafbff;}
  .cs-name{font-size:.84rem;font-weight:700;color:#0f1123;}
  .cs-meta{font-size:.7rem;color:#9094a8;margin-top:2px;}
  .ap-empty{text-align:center;padding:3rem 2rem;color:#9094a8;font-size:.82rem;}
  .cap-loading{display:flex;align-items:center;justify-content:center;height:200px;gap:10px;color:#9094a8;}
  .spinner{width:18px;height:18px;border:2px solid #e4e7f0;border-top-color:#f97316;border-radius:50%;animation:spin .7s linear infinite;}
  @keyframes spin{to{transform:rotate(360deg);}}
  @media(max-width:768px){
    .cap-title{font-size:1.1rem;} .cap-tabs{width:100%;}
    .cap-tab{flex:1;text-align:center;} .module-grid{grid-template-columns:1fr;}
    .filter-bar{flex-direction:column;} .search-wrap{width:100%;} .filter-select{width:100%;}
    .od-module-grid{grid-template-columns:1fr 1fr;}
  }
`;

const ini = (n = '') => n.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

const fmtClasses = (assignedClasses) => {
  if (!assignedClasses || !Array.isArray(assignedClasses) || assignedClasses.length === 0) return 'No classes';
  return assignedClasses.map(c => {
    if (typeof c === 'string') return c;
    if (c && typeof c === 'object') return c.name || c.className || String(c.classId || '');
    return '';
  }).filter(Boolean).join(', ');
};

const CoordAccessPanel = () => {
  const [tab,          setTab]          = useState('teacher');
  const [subTab,       setSubTab]       = useState('role');
  const [access,       setAccess]       = useState(null);
  const [classes,      setClasses]      = useState([]);
  const [teachers,     setTeachers]     = useState([]);
  const [students,     setStudents]     = useState([]);
  const [financeMembers, setFinanceMembers] = useState([]);
  const [librariansList, setLibrariansList] = useState([]);
  const [hostelMembersList, setHostelMembersList] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [search,       setSearch]       = useState('');
  const [filterClass,  setFilterClass]  = useState('');
  const [filterSec,    setFilterSec]    = useState('');
  // Role-level drafts: `teacher:dashboard` -> bool
  const [drafts,       setDrafts]       = useState({});
  // Class-level drafts: `classId-section` -> modules[]
  const [classDraft,   setClassDraft]   = useState({});
  const [openClass,    setOpenClass]    = useState(null);
  // Individual drafts: userId -> modules[]
  const [draftModules, setDraftModules] = useState({});
  const [openDrawer,   setOpenDrawer]   = useState(null);

  const modules = MODULE_DEFS[tab] || [];

  // ── Load access settings + classes ─────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      api.get('/coordinator/access-settings'),
      api.get('/coordinator/classes'),
    ]).then(([a, c]) => {
      setAccess(a.data);
      setClasses(c.data || []);
    }).catch(() => {
      setAccess({ teacherModules: TEACHER_MODULES.map(m => m.key), studentModules: STUDENT_MODULES.map(m => m.key), financeModules: FINANCE_MODULES.map(m => m.key), librarianModules: LIBRARIAN_MODULES.map(m => m.key), hostelModules: HOSTEL_MODULES.map(m => m.key), classOverrides: [], userOverrides: [] });
    }).finally(() => setLoading(false));
  }, []);

  // ── Load individual people ──────────────────────────────────────────────────
  // Students: pass section to API, refetch on section change
  useEffect(() => {
    if (subTab !== 'individual' || tab !== 'student') return;
    setLoading(true);
    api.get(`/coordinator/students?classId=${filterClass}&section=${filterSec}&_=${Date.now()}`)
      .then(r => setStudents(r.data?.students || (Array.isArray(r.data) ? r.data : [])))
      .catch(() => toast.error('Failed to load students'))
      .finally(() => setLoading(false));
  }, [tab, subTab, filterClass, filterSec]);

  // Teachers: never pass section to API — filter sections on frontend only
  useEffect(() => {
    if (subTab !== 'individual' || tab !== 'teacher') return;
    setLoading(true);
    api.get(`/coordinator/teachers?classId=${filterClass}&_=${Date.now()}`)
      .then(r => {
        const data = r.data?.teachers || (Array.isArray(r.data) ? r.data : []);
        setTeachers(data);
      })
      .catch(() => toast.error('Failed to load teachers'))
      .finally(() => setLoading(false));
  }, [tab, subTab, filterClass]); // filterSec intentionally excluded for teachers

  // Finance — flat list, no class scoping
  useEffect(() => {
    if (subTab !== 'individual' || tab !== 'finance') return;
    setLoading(true);
    api.get(`/coordinator/finance-members?_=${Date.now()}`)
      .then(r => setFinanceMembers(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Failed to load finance members'))
      .finally(() => setLoading(false));
  }, [tab, subTab]);

  useEffect(() => {
    if (subTab !== 'individual' || tab !== 'librarian') return;
    setLoading(true);
    api.get(`/coordinator/librarians?_=${Date.now()}`)
      .then(r => setLibrariansList(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Failed to load librarians'))
      .finally(() => setLoading(false));
  }, [tab, subTab]);

  useEffect(() => {
    if (subTab !== 'individual' || tab !== 'hostel_member') return;
    setLoading(true);
    api.get(`/coordinator/hostel-members?_=${Date.now()}`)
      .then(r => setHostelMembersList(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Failed to load hostel members'))
      .finally(() => setLoading(false));
  }, [tab, subTab]);

  const roleModules = access
    ? (access[ROLE_FIELD[tab]] || [])
    : modules.map(m => m.key);

  // ── Role tab helpers ──────────────────────────────────────────────────────
  const isEnabled = (key) => {
    const dk = `${tab}:${key}`;
    if (drafts[dk] !== undefined) return drafts[dk];
    return (roleModules || []).includes(key);
  };

  const toggleModule = (key) => {
    const dk = `${tab}:${key}`;
    setDrafts(prev => ({ ...prev, [dk]: !isEnabled(key) }));
  };

  // Enable All / Disable All — immediately stages all as draft
  const setAllRole = (enable) => {
    const updates = {};
    modules.forEach(m => { updates[`${tab}:${m.key}`] = enable; });
    setDrafts(prev => ({ ...prev, ...updates }));
  };

  const hasRoleChanges = Object.keys(drafts).some(k => k.startsWith(`${tab}:`));

  const submitRoleRequest = async () => {
    const teacherMods = TEACHER_MODULES.map(m => m.key).filter(k => {
      const dk = `teacher:${k}`;
      return drafts[dk] !== undefined ? drafts[dk] : (access?.teacherModules || []).includes(k);
    });
    const studentMods = STUDENT_MODULES.map(m => m.key).filter(k => {
      const dk = `student:${k}`;
      return drafts[dk] !== undefined ? drafts[dk] : (access?.studentModules || []).includes(k);
    });
    const financeMods = FINANCE_MODULES.map(m => m.key).filter(k => {
      const dk = `finance:${k}`;
      return drafts[dk] !== undefined ? drafts[dk] : (access?.financeModules || []).includes(k);
    });
    const librarianMods = LIBRARIAN_MODULES.map(m => m.key).filter(k => {
      const dk = `librarian:${k}`;
      return drafts[dk] !== undefined ? drafts[dk] : (access?.librarianModules || []).includes(k);
    });
    const hostelMods = HOSTEL_MODULES.map(m => m.key).filter(k => {
      const dk = `hostel_member:${k}`;
      return drafts[dk] !== undefined ? drafts[dk] : (access?.hostelModules || []).includes(k);
    });
    const changes = Object.entries(drafts).map(([k, v]) => {
      const [role, key] = k.split(':');
      const mod = MODULE_DEFS[role]?.find(m => m.key === key);
      return { role, module: key, label: mod?.label || key, enabled: v, icon: mod?.icon || '' };
    });
    try {
      setSaving(true);
      await api.post('/coordinator/requests', {
        type: 'access_change',
        payload: { scope: 'role', teacherModules: teacherMods, studentModules: studentMods, financeModules: financeMods, librarianModules: librarianMods, hostelModules: hostelMods, changes },
      });
      toast.success('Request submitted — awaiting admin approval');
      setDrafts({});
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  // ── Class override helpers ────────────────────────────────────────────────
  const getClassOverride = (classId, section) =>
    access?.classOverrides?.find(o => String(o.class) === String(classId) && o.section === (section || ''));

  const submitClassRequest = async (classId, section, mods, className) => {
    const override = getClassOverride(classId, section);
    const changes = modules.map(m => ({
      role: tab, module: m.key, label: m.label, icon: m.icon,
      enabled: mods.includes(m.key),
      wasEnabled: (override?.modules || roleModules).includes(m.key),
    })).filter(c => c.enabled !== c.wasEnabled);
    try {
      setSaving(true);
      await api.post('/coordinator/requests', {
        type: 'access_change',
        payload: { scope: 'class', classId, section: section || '', className, modules: mods, role: tab, changes },
      });
      toast.success('Request submitted — awaiting admin approval');
      setClassDraft(prev => { const n = { ...prev }; delete n[`${classId}-${section}`]; return n; });
      setOpenClass(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  // Submit "remove class override" request
  const submitRemoveClassRequest = async (classId, section, className) => {
    try {
      setSaving(true);
      await api.post('/coordinator/requests', {
        type: 'access_change',
        payload: { scope: 'class_remove', classId, section: section || '', className, role: tab },
      });
      toast.success('Remove override request submitted — awaiting admin approval');
      setOpenClass(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  // ── Individual override helpers ───────────────────────────────────────────
  const openUserDrawer = (userId, currentMods) => {
    if (openDrawer === String(userId)) { setOpenDrawer(null); return; }
    setOpenDrawer(String(userId));
    setDraftModules(prev => ({ ...prev, [String(userId)]: currentMods || [...roleModules] }));
  };

  const toggleDraftModule = (userId, key) => {
    setDraftModules(prev => {
      const cur = prev[String(userId)] || [];
      return { ...prev, [String(userId)]: cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key] };
    });
  };

  const submitIndividualRequest = async (person) => {
    // Normalise: admin list uses person.userId; coordinator list uses person.user?._id or person._id
    const userId   = String(person.userId || person.user?._id || person._id);
    const name     = person.name || person.user?.name || '';
    const mods     = draftModules[userId] || roleModules;
    const changes  = modules.map(m => ({
      role: tab, module: m.key, label: m.label, icon: m.icon,
      enabled: mods.includes(m.key),
      wasEnabled: roleModules.includes(m.key),
    })).filter(c => c.enabled !== c.wasEnabled);
    try {
      setSaving(true);
      await api.post('/coordinator/requests', {
        type: 'access_change',
        payload: { scope: 'individual', userId, personName: name, role: tab, modules: mods, changes },
      });
      toast.success('Request submitted — awaiting admin approval');
      setOpenDrawer(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const submitRemoveIndividualRequest = async (person) => {
    const userId = String(person.userId || person.user?._id || person._id);
    const name   = person.name || person.user?.name || '';
    try {
      setSaving(true);
      await api.post('/coordinator/requests', {
        type: 'access_change',
        payload: { scope: 'individual_remove', userId, personName: name, role: tab },
      });
      toast.success('Remove override request submitted — awaiting admin approval');
      setOpenDrawer(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  // ── Filtered people list ──────────────────────────────────────────────────
  const rawPeople = tab === 'teacher' ? teachers : tab === 'student' ? students : tab === 'finance' ? financeMembers : tab === 'librarian' ? librariansList : hostelMembersList;

  // Normalise coordinator API shape → same flat shape admin uses
  const people = rawPeople.map(p => {
    if (tab === 'finance' || tab === 'librarian' || tab === 'hostel_member') {
      // These list endpoints have no override info — overlay it client-side
      const userId   = String(p._id || p.userId);
      const override = access?.userOverrides?.find(o => String(o.user) === userId);
      return {
        ...p,
        userId,
        name:  p.name  || '',
        email: p.email || '',
        hasOverride:  !!override,
        overrideType: override ? 'user' : null,
        modules:      override ? override.modules : null,
      };
    } else if (tab === 'teacher') {
      return {
        ...p,
        userId:         String(p.user?._id || p._id),
        name:           p.user?.name  || p.name  || '',
        email:          p.user?.email || p.email || '',
        employeeId:     p.employeeId  || '',
        assignedClasses: p.assignedClasses || [],
        assignedSections: p.assignedSections || [],
        // hasOverride / overrideType may come from backend if access endpoint returns it
        hasOverride:    p.hasOverride    || false,
        overrideType:   p.overrideType   || null,
        modules:        p.modules        || null,
      };
    } else {
      return {
        ...p,
        userId:      String(p.user?._id || p._id),
        name:        p.user?.name  || p.name  || '',
        email:       p.user?.email || p.email || '',
        admissionNo: p.admissionNo || p.admission_no || '',
        rollNo:      p.rollNo      || p.roll_no      || '',
        class:       p.class?.name || p.className    || '',
        section:     p.section     || '',
        hasOverride: p.hasOverride    || false,
        overrideType:p.overrideType   || null,
        modules:     p.modules        || null,
      };
    }
  });

  const filtered = people.filter(p => {
    // Teachers: section filter is frontend-only
    if (filterSec && tab === 'teacher') {
      const secs = p.assignedSections || [];
      if (!secs.includes(filterSec)) return false;
    }
    if (!search) return true;
    const s = search.toLowerCase();
    return p.name?.toLowerCase().includes(s) ||
           p.email?.toLowerCase().includes(s) ||
           p.employeeId?.toLowerCase().includes(s) ||
           p.admissionNo?.toLowerCase().includes(s);
  });

  // Sections dropdown — always derived from class definition, never from filtered list
  const sections = filterClass
    ? (classes.find(c => String(c._id) === filterClass)?.sections || [])
    : [];

  if (loading && !access) return (
    <div className="cap"><style>{style}</style>
      <div className="cap-loading"><div className="spinner" />Loading…</div>
    </div>
  );

  return (
    <div className="cap"><style>{style}</style>
      <div className="cap-header">
        <div>
          <div className="cap-title">Access Control Panel</div>
          <div className="cap-sub">View current access · submit requests to change</div>
        </div>
      </div>
      <div className="req-banner">📋 All changes require admin approval before taking effect.</div>

      {/* Role tabs */}
      <div className="cap-tabs">
        <button className={`cap-tab ${tab === 'teacher' ? 'active' : ''}`}
          onClick={() => { setTab('teacher'); setSubTab('role'); setOpenDrawer(null); setDrafts({}); }}>
          👨‍🏫 Teachers
        </button>
        <button className={`cap-tab ${tab === 'student' ? 'active' : ''}`}
          onClick={() => { setTab('student'); setSubTab('role'); setOpenDrawer(null); setDrafts({}); }}>
          🎓 Students
        </button>
        <button className={`cap-tab ${tab === 'finance' ? 'active' : ''}`}
          onClick={() => { setTab('finance'); setSubTab('role'); setOpenDrawer(null); setDrafts({}); }}>
          💰 Finance
        </button>
        <button className={`cap-tab ${tab === 'librarian' ? 'active' : ''}`}
          onClick={() => { setTab('librarian'); setSubTab('role'); setOpenDrawer(null); }}>
          📚 Library
        </button>
        <button className={`cap-tab ${tab === 'hostel_member' ? 'active' : ''}`}
          onClick={() => { setTab('hostel_member'); setSubTab('role'); setOpenDrawer(null); }}>
          🏨 Hostel
        </button>
      </div>

      {/* Sub tabs */}
      <div className="sub-tabs">
        {[
          { key: 'role',       label: `Default (All ${ROLE_LABEL[tab]})` },
          ...(tab === 'teacher' || tab === 'student' ? [{ key: 'class', label: 'By Class / Section' }] : []),
          { key: 'individual', label: 'Individual Override' },
        ].map(st => (
          <button key={st.key} className={`sub-tab ${subTab === st.key ? 'active' : ''}`}
            onClick={() => { setSubTab(st.key); setOpenDrawer(null); }}>
            {st.label}
          </button>
        ))}
      </div>

      {/* ── ROLE DEFAULTS ─────────────────────────────────────────────────── */}
      {subTab === 'role' && (
        <div className="role-card">
          <div className="role-card-head">
            <div>
              <div className="role-card-title">Default Access — All {ROLE_LABEL[tab]}</div>
              <div className="role-card-sub">
                {tab === 'teacher' || tab === 'student'
                  ? 'These apply to everyone unless overridden by class or individual settings'
                  : 'These apply to everyone unless overridden by individual settings'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {/* Enable All / Disable All — stage as draft, not immediate */}
              <button className="btn btn-ghost btn-sm" onClick={() => setAllRole(false)}>Disable All</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setAllRole(true)}>Enable All</button>
              {hasRoleChanges && (
                <>
                  <button className="btn btn-ghost btn-sm" onClick={() => setDrafts({})}>Reset</button>
                  <button className="btn btn-orange btn-sm" onClick={submitRoleRequest} disabled={saving}>
                    <PaperAirplaneIcon />
                    {saving ? 'Submitting…' : `Submit Request (${Object.keys(drafts).filter(k => k.startsWith(`${tab}:`)).length} changes)`}
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="module-grid">
            {modules.map(m => {
              const enabled = isEnabled(m.key);
              const dk      = `${tab}:${m.key}`;
              const isDraft = drafts[dk] !== undefined;
              return (
                <div key={m.key} className={`module-item ${enabled ? 'enabled' : 'disabled'}`}
                  style={isDraft ? { outline: '2px solid #f97316', outlineOffset: 1 } : {}}>
                  <div className="module-item-left">
                    <span className="module-icon">{m.icon}</span>
                    <div>
                      <div className="module-label">{m.label}</div>
                      <div className="module-desc">
                        {isDraft
                          ? <span style={{ color: '#f97316', fontWeight: 600 }}>⏳ Pending request</span>
                          : m.desc}
                      </div>
                    </div>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" checked={enabled} onChange={() => toggleModule(m.key)} />
                    <span className="toggle-slider" />
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── CLASS / SECTION ───────────────────────────────────────────────── */}
      {subTab === 'class' && (
        <>
          <div style={{ fontSize: '.8rem', color: '#6b7194', marginBottom: '1rem', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '10px 14px' }}>
            📌 Class/section overrides apply to all {tab === 'teacher' ? 'teachers' : 'students'} in that class or section. They take priority over role defaults but individual overrides take priority over these.
          </div>
          {classes.length === 0 ? <div className="ap-empty">No classes found.</div>
            : classes.map(cls => {
              const clsSections = cls.sections || [];
              const clsKey      = `${cls._id}-`;
              const clsOverride = getClassOverride(cls._id, '');
              const isClsOpen   = openClass === clsKey;
              return (
                <div key={cls._id} className="class-section">
                  <div className="class-section-head" onClick={() => setOpenClass(isClsOpen ? null : clsKey)}>
                    <div>
                      <div className="cs-name">{cls.name}</div>
                      <div className="cs-meta">
                        {clsSections.length} section{clsSections.length !== 1 ? 's' : ''} · {' '}
                        {clsOverride
                          ? <span style={{ color: '#f59e0b', fontWeight: 600 }}>⚠ Has override ({clsOverride.modules?.length} modules)</span>
                          : <span>Using role defaults</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: '.72rem', color: '#f97316', fontWeight: 600 }}>{isClsOpen ? 'Close' : 'Configure'}</span>
                      <ChevronDownIcon style={{ width: 15, height: 15, stroke: '#9094a8', transform: isClsOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
                    </div>
                  </div>
                  {isClsOpen && (
                    <div className="override-drawer">
                      <div className="od-title">All sections of {cls.name}</div>
                      <div className="od-module-grid">
                        {modules.map(m => {
                          const cur = classDraft[clsKey] ?? (clsOverride?.modules || roleModules);
                          const on  = cur.includes(m.key);
                          return (
                            <div key={m.key} className={`od-module ${on ? 'on' : 'off'}`}
                              onClick={() => setClassDraft(prev => {
                                const c = prev[clsKey] ?? (clsOverride?.modules || [...roleModules]);
                                return { ...prev, [clsKey]: c.includes(m.key) ? c.filter(k => k !== m.key) : [...c, m.key] };
                              })}>
                              <span className="dot" /><span>{m.icon} {m.label}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="od-actions">
                        <button className="btn btn-orange btn-sm" disabled={saving} onClick={() => {
                          const mods = classDraft[clsKey] ?? (clsOverride?.modules || [...roleModules]);
                          submitClassRequest(cls._id, '', mods, cls.name);
                        }}>
                          <PaperAirplaneIcon />{saving ? 'Submitting…' : 'Submit Request'}
                        </button>
                        {clsOverride && (
                          <button className="btn btn-ghost btn-sm" disabled={saving}
                            onClick={() => submitRemoveClassRequest(cls._id, '', cls.name)}>
                            <ArrowPathIcon />Reset to Default
                          </button>
                        )}
                        <button className="btn btn-ghost btn-sm" onClick={() => setOpenClass(null)}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {/* Per-section rows */}
                  {clsSections.map(sec => {
                    const secKey      = `${cls._id}-${sec}`;
                    const secOverride = getClassOverride(cls._id, sec);
                    const isSecOpen   = openClass === secKey;
                    return (
                      <div key={sec} style={{ borderTop: '1px solid #f3f5fb' }}>
                        <div className="class-section-head" style={{ paddingLeft: 32, background: '#fafbff' }}
                          onClick={() => setOpenClass(isSecOpen ? null : secKey)}>
                          <div>
                            <div className="cs-name" style={{ fontSize: '.8rem' }}>Section {sec}</div>
                            <div className="cs-meta">
                              {secOverride
                                ? <span style={{ color: '#f59e0b', fontWeight: 600 }}>⚠ Has override ({secOverride.modules?.length} modules)</span>
                                : clsOverride
                                ? <span style={{ color: '#8b5cf6' }}>Using class override</span>
                                : <span>Using role defaults</span>}
                            </div>
                          </div>
                          <span style={{ fontSize: '.72rem', color: '#f97316', fontWeight: 600 }}>{isSecOpen ? 'Close' : 'Configure'}</span>
                        </div>
                        {isSecOpen && (
                          <div className="override-drawer">
                            <div className="od-title">{cls.name} — Section {sec}</div>
                            <div className="od-module-grid">
                              {modules.map(m => {
                                const base = secOverride?.modules || clsOverride?.modules || roleModules;
                                const cur  = classDraft[secKey] ?? base;
                                const on   = cur.includes(m.key);
                                return (
                                  <div key={m.key} className={`od-module ${on ? 'on' : 'off'}`}
                                    onClick={() => setClassDraft(prev => {
                                      const c = prev[secKey] ?? base;
                                      return { ...prev, [secKey]: c.includes(m.key) ? c.filter(k => k !== m.key) : [...c, m.key] };
                                    })}>
                                    <span className="dot" /><span>{m.icon} {m.label}</span>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="od-actions">
                              <button className="btn btn-orange btn-sm" disabled={saving} onClick={() => {
                                const base = secOverride?.modules || clsOverride?.modules || [...roleModules];
                                const mods = classDraft[secKey] ?? base;
                                submitClassRequest(cls._id, sec, mods, `${cls.name} Section ${sec}`);
                              }}>
                                <PaperAirplaneIcon />{saving ? 'Submitting…' : 'Submit Request'}
                              </button>
                              {secOverride && (
                                <button className="btn btn-ghost btn-sm" disabled={saving}
                                  onClick={() => submitRemoveClassRequest(cls._id, sec, `${cls.name} Section ${sec}`)}>
                                  <ArrowPathIcon />Reset
                                </button>
                              )}
                              <button className="btn btn-ghost btn-sm" onClick={() => setOpenClass(null)}>Cancel</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
        </>
      )}

      {/* ── INDIVIDUAL ────────────────────────────────────────────────────── */}
      {subTab === 'individual' && (
        <>
          <div className="filter-bar">
            <div className="search-wrap">
              <MagnifyingGlassIcon />
              <input className="search-input" placeholder={`Search ${ROLE_LABEL[tab]?.toLowerCase()}…`}
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            {(tab === 'teacher' || tab === 'student') && (
              <select className="filter-select" value={filterClass} onChange={e => { setFilterClass(e.target.value); setFilterSec(''); }}>
                <option value="">All Classes</option>
                {sortByName(classes).map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            )}
            {(tab === 'teacher' || tab === 'student') && sections.length > 0 && (
              <select className="filter-select" value={filterSec} onChange={e => setFilterSec(e.target.value)}>
                <option value="">All Sections</option>
                {sortAsc(sections).map(s => <option key={s} value={s}>Section {s}</option>)}
              </select>
            )}
          </div>

          <div className="panel">
            <div className="panel-head">
              <div className="panel-title">{ROLE_LABEL[tab]} ({filtered.length})</div>
              <div style={{ fontSize: '.72rem', color: '#9094a8' }}>Click Configure to request individual access changes</div>
            </div>

            {loading
              ? <div className="cap-loading"><div className="spinner" />Loading…</div>
              : filtered.length === 0
              ? <div className="ap-empty">No {ROLE_LABEL[tab]?.toLowerCase()} found.</div>
              : filtered.map(person => {
                const userId = person.userId;
                const isOpen = openDrawer === userId;
                const draft  = draftModules[userId] || (person.modules || [...roleModules]);
                const name   = person.name;

                return (
                  <div key={person._id}>
                    <div className="person-row">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                        <div className="p-avatar">{ini(name)}</div>
                        <div style={{ minWidth: 0 }}>
                          <div className="p-name">{name}</div>
                          <div className="p-meta">
                            {tab === 'teacher'
                              ? `${person.email} · ${person.employeeId} · ${fmtClasses(person.assignedClasses)}`
                              : tab === 'student'
                              ? `${person.email} · ${person.class} Sec ${person.section} · Roll ${person.rollNo}`
                              : person.email}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        {person.hasOverride
                          ? <span className={`p-badge ${person.overrideType === 'user' ? 'override' : person.overrideType === 'section' ? 'section-ov' : 'class-ov'}`}>
                              {person.overrideType === 'user'    ? '✏ Individual Override'
                               : person.overrideType === 'section' ? '📌 Section Override'
                               : '📁 Class Override'}
                            </span>
                          : <span className="p-badge default">Using Default</span>}
                        <button className="btn btn-ghost btn-sm"
                          onClick={() => openUserDrawer(userId, person.modules || [...roleModules])}>
                          {isOpen ? 'Close' : 'Configure'}
                        </button>
                      </div>
                    </div>

                    {isOpen && (
                      <div className="override-drawer">
                        <div className="od-title">Individual Access — {name}</div>
                        <div className="od-module-grid">
                          {modules.map(m => {
                            const on = draft.includes(m.key);
                            return (
                              <div key={m.key} className={`od-module ${on ? 'on' : 'off'}`}
                                onClick={() => toggleDraftModule(userId, m.key)}>
                                <span className="dot" /><span>{m.icon} {m.label}</span>
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => setDraftModules(prev => ({ ...prev, [userId]: modules.map(m => m.key) }))}>Select All</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setDraftModules(prev => ({ ...prev, [userId]: [] }))}>Clear All</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setDraftModules(prev => ({ ...prev, [userId]: [...roleModules] }))}>Reset to Default</button>
                        </div>
                        <div className="od-actions">
                          <button className="btn btn-orange btn-sm" onClick={() => submitIndividualRequest(person)} disabled={saving}>
                            <PaperAirplaneIcon />{saving ? 'Submitting…' : 'Submit Request'}
                          </button>
                          {/* Show Remove Override only if this person has a user-level override */}
                          {person.hasOverride && person.overrideType === 'user' && (
                            <button className="btn btn-ghost btn-sm" disabled={saving}
                              onClick={() => submitRemoveIndividualRequest(person)}>
                              <ArrowPathIcon />Remove Override
                            </button>
                          )}
                          <button className="btn btn-ghost btn-sm" onClick={() => setOpenDrawer(null)}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </>
      )}
    </div>
  );
};

export default CoordAccessPanel;